/**
 * PluginContext - React Context for Plugin SDK Injection
 * 
 * Provides plugin components with access to:
 * - Plugin-specific SDK with scoped authorization
 * - Plugin metadata (id, manifest)
 * - PDP application scope
 * 
 * @example
 * ```tsx
 * // In plugin component
 * const React = (globalThis as any).React;
 * const { usePlugin } = (globalThis as any).PluginSDK || {};
 * 
 * export function MyPluginView() {
 *   const { sdk, pluginId } = usePlugin();
 *   
 *   const handleAction = async () => {
 *     const result = await sdk.authz.evaluate({
 *       resource: { type: 'my-resource', id: 'res-123' },
 *       action: { name: 'delete' }
 *     });
 *     // PDP call automatically includes pdp_application from manifest
 *   };
 *   
 *   return <button onClick={handleAction}>Delete</button>;
 * }
 * ```
 */

import React, { createContext, useContext } from 'react';
import type { PluginManifest, PluginSDK } from './types';

export interface PluginContextValue {
	/**
	 * Plugin identifier
	 */
	pluginId: string;
	
	/**
	 * Plugin manifest with all configuration
	 */
	manifest: PluginManifest;
	
	/**
	 * Plugin SDK instance with scoped authorization
	 */
	sdk: PluginSDK;
}

const PluginContext = createContext<PluginContextValue | null>(null);

/**
 * Provider component that injects plugin SDK into the component tree
 * Used by the host app when mounting plugin routes/widgets
 */
export function PluginProvider({ 
	value, 
	children 
}: { 
	value: PluginContextValue; 
	children: React.ReactNode 
}) {
	return (
		<PluginContext.Provider value={value}>
			{children}
		</PluginContext.Provider>
	);
}

/**
 * Hook to access plugin SDK and metadata from within a plugin component
 * @throws {Error} if called outside of PluginProvider
 */
export function usePlugin(): PluginContextValue {
	const ctx = useContext(PluginContext);
	if (!ctx) {
		throw new Error(
			'usePlugin must be used within a PluginProvider. ' +
			'This hook can only be called from within a plugin component.'
		);
	}
	return ctx;
}

/**
 * Optional hook that returns plugin context if available, null otherwise
 * Useful for components that may or may not be within a plugin
 */
export function usePluginOptional(): PluginContextValue | null {
	return useContext(PluginContext);
}

// Expose globally for plugins that cannot use ES6 imports
// Plugins can access via: const { usePlugin } = (globalThis as any).PluginSDK;
if (typeof window !== 'undefined') {
	(window as any).PluginSDK = {
		usePlugin,
		usePluginOptional,
	};
}

